import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, mergeMap, switchMap, catchError, tap, skipWhile, take, withLatestFrom, filter } from 'rxjs/operators';
import { of, empty, combineLatest, EMPTY } from 'rxjs';
import { Store, select } from '@ngrx/store';
import { Update } from '@ngrx/entity';

import * as ActivityActions from './activity.actions';
import * as VisitorsActions from './visitors.actions';
import * as ToastrActions  from '../../core/store/toastr.actions';
import { ConversationsService } from '../../core/services/conversations.service';
import { AppState } from '../../core/core.state';
import { selectCurrentVisitor, selectSelectedAgent, selectSelectedVisitor, selectVisitorById } from './visitors.selectors';
import { Visitor } from '../../models';
import { currentAvailableStatus, currentProperties, currentUser } from '../../core/auth/auth.selectors';
import { isNil, isNumber } from 'lodash-es';
import { selectSelectedProperty, selectViewMode } from 'src/app/core/store/layout.selectors';
import { HttpErrorResponse } from '@angular/common/http';
import { I18nService } from '../../core/i18n';

@Injectable()
export class ActivityEffects {
    private i18n = inject(I18nService);

    loadVisit = createEffect(() => this.actions$.pipe(
        ofType(ActivityActions.VisitRequested),
        mergeMap(action => this.converseService.getVisit( action.id )),
        switchMap( visit => {
            
            // if the new visit contains wc data, update the visitor as well
            if (visit.model.extra_wc?.items?.length > 0 || visit.model.extra_wc?.email || visit.model.extra_wc?.name ){
                
                
                const visitor_update: Update<Visitor> = {
                    id: visit.model.visitor_id,
                    changes: {
                        last_shop_visit: {...visit.model.extra_wc, created_at: visit.model.created_at},    
                    }
                };

                if (visit.model.extra_wc.email){
                    visitor_update.changes = {
                        ...visitor_update.changes, 
                        email: visit.model.extra_wc.email,
                        name: visit.model.extra_wc.email.substring(0, visit.model.extra_wc.email.lastIndexOf("@"))
                    };
                } 

                if (visit.model.extra_wc.name){
                    visitor_update.changes = {
                        ...visitor_update.changes, 
                        name: visit.model.extra_wc.name
                    };
                } 

                return [
                    ActivityActions.VisitLoaded( {Visit: visit.model} ),
                    VisitorsActions.VisitorUpdateSuccess({ visitor: visitor_update})
                ];
            }

            return [ActivityActions.VisitLoaded( {Visit: visit.model} )];
        })
    ));

    loadActivityOfVisitor = createEffect(() => this.actions$.pipe(
        ofType(ActivityActions.ActivitiesOfVisitorRequested),
        mergeMap(action => this.converseService.getActivities( action.visitor_id )),
        map( a => ActivityActions.ActivitiesOfVisitorLoaded( {Activities: a.model} ) )
    ));

    

    messageReceived$ = createEffect( () => this.actions$.pipe(
        ofType(ActivityActions.MessageActivityReceived),
        mergeMap(action => this.converseService.getMessage( action.message_id )),
        switchMap(message => combineLatest([
            of(message),
            this.store.select(currentUser),
            this.store.select(selectViewMode),
            this.store.select(currentProperties),
            this.store.select(selectSelectedAgent),
            this.store.select(selectSelectedProperty),
        ]).pipe(take(1))),
        // only proceed if the message belongs to a visitor that is in a property accessible by the current user
        filter( ([message, current_user]) =>  current_user.properties.find(p => p.id == message.model.property_id) != undefined  ) , 
        switchMap(([message, current_user, view_mode, properties, selected_agent, property_id]) => {
            var p = (property_id != 0) ? property_id : properties.map(q => q.id).join(',');   

            return this.converseService.visitorExistsinView(message.model.visitor_id, p, view_mode, selected_agent?.id).pipe(
                map(visitorExists => ({ message, current_user, view_mode, visitorExistsinView: true })),
                catchError((err: HttpErrorResponse) => {
                    if (err.status === 404) return of({ message, current_user, view_mode, visitorExistsinView: false });
                    return EMPTY;
                })
            );
             
        }),
        mergeMap( ({message, current_user, view_mode, visitorExistsinView}) => {
            
            if (visitorExistsinView) {
                return [
                    VisitorsActions.VisitorRequested( { visitor_id: message.model.visitor_id }),
                    ActivityActions.MessageActivityLoaded({Message: message.model}),
                    ActivityActions.MessageNotify({message: message.model})
                ];
            } else {
                return [
                    ActivityActions.MessageActivityLoaded({Message: message.model}),
                    ActivityActions.MessageNotify({message: message.model})
                ];
            }


        })
    ));

    messageNotify$ = createEffect( () => this.actions$.pipe(
        ofType(ActivityActions.MessageNotify),
        switchMap(action =>
            combineLatest([
                    of(action.message),
                    this.store.pipe( select(selectVisitorById(action.message.visitor_id)), take(1)),
                    this.store.pipe( select(selectCurrentVisitor), take(1)),
                    this.store.pipe( select(currentAvailableStatus), take(1))          
                ])
        ),
        switchMap( ([m, v, s, a]) => {

            // Show notification if user is available and not isBot
            if (a == 1 && !m.is_bot){
                this.converseService.show_notification(
                    m.message, 
                    this.i18n.t('inbox.activity.visitor_id', 'Visitor #{{id}}', {id: m.visitor_id.substr(0,2).toUpperCase()}) 
                );
                this.converseService.play_notification();
                this.converseService.flash_notification();
                this.converseService.loop_sound(v);
            }

            // increase the unread count if the visitor is not the one selected, and if the visitor exists and visible in the store
            if ( m.visitor_id != s && (v != undefined) ) {
                const visitor_update: Update<Visitor> = {
                    id: m.visitor_id,
                    changes: { unread_count: (v.unread_count) ? v.unread_count + 1 : 1 }
                };                    
                return of(VisitorsActions.VisitorUnreadCount({visitor: visitor_update})) ;
            } else {
                return EMPTY;
            }

        })    
    ));

    // apply same logic as messageReceived$ to agent messages, i need to check the view first
    agentMessageReceived$ = createEffect( () => this.actions$.pipe(
        ofType(ActivityActions.AgentActivityReceived),
        mergeMap(action => this.converseService.getMessage( action.message_id )),
        mergeMap( res=> combineLatest([
            of(res.model),
            this.store.pipe( select(selectVisitorById(res.model.visitor_id)), take(1)),
            this.store.pipe( select(selectCurrentVisitor), take(1)),
        ])),
        mergeMap( ([message, visitor_message, selected_vid]) => {
            var actions = [];
            if (message.visitor_id == selected_vid) {
                actions.push(ActivityActions.MessageActivityLoaded({Message: message}));
            }

            if (visitor_message.agents != message.agent_id){
                const visitor_update: Update<Visitor> = {
                    id: visitor_message.id,
                    changes: { 
                        agents: this.appendAgent(message.agent_id, visitor_message.agents),
                        is_attended: true 
                    }
                }; 
                actions.push(VisitorsActions.VisitorUpdateSuccess({visitor: visitor_update})); 
            }

            if (actions.length == 0) return EMPTY;
            return actions;

        })
    ));

    emailSession = createEffect(() => this.actions$.pipe(
        ofType(ActivityActions.EmailSessionRequest),
        mergeMap(action => this.converseService.emailSession( action.session_id, action.visitor_id).pipe(
            switchMap(res => [
                ToastrActions.showToastrSuccess({message:this.i18n.t('inbox.activity.session_transcript_scheduled_to_be_sent', 'Session Transcript scheduled to be sent.'), title: ''})
            ]),
            catchError( err => [
                ToastrActions.showToastrError({message: this.i18n.t('inbox.activity.error_while_scheduling_sending', 'Error while scheduling sending.') , title: this.i18n.t('common.something_went_wrong', 'Something went wrong')})
            ])
            )
        ) 
    ));

    emailSessiontoVisitor = createEffect(() => this.actions$.pipe(
        ofType(ActivityActions.EmailSessionToVisitorRequest),
        mergeMap(action => this.converseService.emailSession( action.session_id, action.visitor_id, "visitor").pipe(
            switchMap(res => [
                ToastrActions.showToastrSuccess({message:this.i18n.t('inbox.activity.session_transcript_scheduled_to_be_sent_2', 'Session Transcript scheduled to be sent to visitor.'), title: ''})
            ]),
            catchError( err => [
                ToastrActions.showToastrError({message: this.i18n.t('inbox.activity.error_while_scheduling_sending', 'Error while scheduling sending.') , title: this.i18n.t('common.something_went_wrong', 'Something went wrong')})
            ])
            )
        ) 
    ));

    reengageChatbot = createEffect(() => this.actions$.pipe(
        ofType(ActivityActions.ReengageChatbotRequest),
        mergeMap(action => this.converseService.reengageChatbot(action.visitor_id).pipe(
            switchMap(res => [
                ToastrActions.showToastrSuccess({message:this.i18n.t('inbox.activity.chatbot_will_now_re_engage_with', 'Chatbot will now re-engage with the visitor.'), title: ''})
            ]),
            catchError( err => [
                ToastrActions.showToastrError({message: this.i18n.t('inbox.activity.error_while_re_engaging_chatbot', 'Error while re-engaging chatbot.') , title: this.i18n.t('common.something_went_wrong', 'Something went wrong')})
            ])
            )
        ) 
    ));


    closeSession = createEffect(() => this.actions$.pipe(
        ofType(ActivityActions.CloseChatSessionRequest),
        mergeMap(action => this.converseService.closeSession(action.visitor_id, action.notify_user, action.request_feedback).pipe(
            switchMap(res => [
                VisitorsActions.VisitorRemove({visitor_id: res.model.id}),
                VisitorsActions.VisitorSelected({visitor_id: ""})
            ]),
            catchError( err => [
                ToastrActions.showToastrError({message: this.i18n.t('inbox.activity.error_closing_the_session', 'Error closing the session.') , title: this.i18n.t('common.something_went_wrong', 'Something went wrong')})
            ])
            )
        )
    ));

    deleteSessions = createEffect(() => this.actions$.pipe(
        ofType(ActivityActions.DeleteSessionsRequest),
        mergeMap(action => this.converseService.deleteSessions(action.visitor_id).pipe(
            switchMap(res => [
                VisitorsActions.VisitorRemove({visitor_id: res.model.id}),
                VisitorsActions.VisitorSelected({visitor_id: ""})
            ]),
            catchError( err => [
                ToastrActions.showToastrError({message: this.i18n.t('inbox.activity.error_deleting_sessions', 'Error deleting sessions.') , title: this.i18n.t('common.something_went_wrong', 'Something went wrong')})
            ])
            )
        )
    ));

    /* SignarR 
    ** @desc Send agent message to Signalr   
    */
   sendMessage$ = createEffect( () => this.actions$.pipe(
    ofType(ActivityActions.SendMessage),
    switchMap(action => combineLatest([
        of(action.Message),
        this.converseService.send(action.Message),
        this.store.pipe(select(selectVisitorById(action.Message.visitor_id)), take(1))
    ])),
    switchMap( ([m, res, v]) => {

            const visitor_update: Update<Visitor> = {
                id: v.id,
                changes: { 
                    is_attended: true,
                    agents: this.appendAgent(m.agent_id, v.agents)
                }
            };                    
        
            return [
                VisitorsActions.VisitorUpdateSuccess({visitor: visitor_update}),
                ActivityActions.MessageActivityLoaded({Message: m})
            ]

    })
    ));

    sendMessageFailed$ = createEffect( () => this.actions$.pipe(
        ofType(ActivityActions.SendMessageFailed),
        mergeMap(action => [
                ToastrActions.showToastrError({message: action.error , title: this.i18n.t('common.something_went_wrong', 'Something went wrong')})
            ]
        )
    ));

    appendAgent(agent, agents){

        // if agents is already empty return the new agent
        if (!agents) return agent;

        var ags = agents.split(",");
        if (!ags.includes(agent)) ags.push(agent);

        return ags.join(",");
    }

    constructor(
        private converseService: ConversationsService,
        private actions$: Actions,
        private store: Store<AppState>
        ) { 
        console.debug("ActivityEffects is constructed")
    }

}